import React, { useMemo, useState } from 'react';
import type { DailyOverlayDayParsed } from '../services/analyticsApi';

const DAY_COLORS = ['#0a84ff', '#30d158', '#ff9f0a', '#bf5af2', '#ff453a', '#64d2ff', '#00c7be', '#5e5ce6'];
const WEEKDAY_COLORS = ['#ff453a', '#ff9f0a', '#ffd60a', '#32ade6', '#00c7be', '#0a84ff', '#bf5af2'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Props {
  days: DailyOverlayDayParsed[];
  targetMin?: number;
  targetMax?: number;
}

const DailyOverlayChart: React.FC<Props> = ({ days, targetMin = 70, targetMax = 180 }) => {
  const [viewMode, setViewMode] = useState<'individual' | 'weekday'>('individual');
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set(days.map(d => d.date)));
  const [selectedWeekdays, setSelectedWeekdays] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5, 6]));

  const w = 720;
  const h = 400;
  const padL = 44;
  const padR = 16;
  const padT = 14;
  const padB = 40; // More padding for X-axis labels
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const yAt = (val: number) => {
    const minG = 40;
    const maxG = 400;
    const clamped = Math.max(minG, Math.min(maxG, val));
    return padT + innerH - ((clamped - minG) / (maxG - minG)) * innerH;
  };

  const xAt = (hour: number) => {
    return padL + (hour / 23) * innerW;
  };

  const toggleDay = (date: string) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const toggleWeekday = (idx: number) => {
    setSelectedWeekdays(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const aggregatedWeekdays = useMemo(() => {
    if (viewMode !== 'weekday') return [];
    
    return Array.from(selectedWeekdays).map(idx => {
      const matchingDays = days.filter(d => {
        const date = new Date(d.date + 'T12:00:00Z'); // Roughly mid-day UTC avoids timezone shifts
        return date.getDay() === idx;
      });

      const hourlyData: { hour: number; avg: number | null }[] = [];
      for (let h = 0; h < 24; h++) {
        let sum = 0;
        let count = 0;
        for (const day of matchingDays) {
          const hr = day.hours.find(x => x.hour === h);
          if (hr && hr.avg != null) {
            sum += hr.avg;
            count++;
          }
        }
        hourlyData.push({ hour: h, avg: count > 0 ? sum / count : null });
      }
      return { idx, name: WEEKDAY_NAMES[idx], color: WEEKDAY_COLORS[idx], hours: hourlyData };
    });
  }, [days, selectedWeekdays, viewMode]);

  const paths = useMemo(() => {
    if (viewMode === 'individual') {
      return days.filter(d => selectedDays.has(d.date)).map((d, index) => {
        const pts: string[] = [];
        for (const hr of d.hours) {
          if (hr.avg != null) {
            pts.push(`${xAt(hr.hour).toFixed(1)},${yAt(hr.avg).toFixed(1)}`);
          }
        }
        return {
          id: d.date,
          name: d.date,
          color: DAY_COLORS[index % DAY_COLORS.length],
          d: pts.length > 1 ? `M ${pts.join(' L ')}` : '',
        };
      });
    } else {
      return aggregatedWeekdays.map(wd => {
        const pts: string[] = [];
        for (const hr of wd.hours) {
          if (hr.avg != null) {
            pts.push(`${xAt(hr.hour).toFixed(1)},${yAt(hr.avg).toFixed(1)}`);
          }
        }
        return {
          id: wd.name,
          name: wd.name,
          color: wd.color,
          d: pts.length > 1 ? `M ${pts.join(' L ')}` : '',
        };
      });
    }
  }, [days, selectedDays, aggregatedWeekdays, viewMode]);

  if (days.length === 0) {
    return <div className="analytics-chart-empty">No daily data to visualize.</div>;
  }

  return (
    <div className="daily-overlay-chart-container" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: '720px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
        <button 
           className={`mode-btn ${viewMode === 'individual' ? 'active' : ''}`} 
           onClick={() => setViewMode('individual')}
           style={{ padding: '0.4rem 1rem', borderRadius: '16px', border: '1px solid var(--border-light)', background: viewMode === 'individual' ? 'var(--bg-layer2)' : 'transparent', color: 'var(--text-primary)' }}
        >
          Individual Days
        </button>
        <button 
           className={`mode-btn ${viewMode === 'weekday' ? 'active' : ''}`} 
           onClick={() => setViewMode('weekday')}
           style={{ padding: '0.4rem 1rem', borderRadius: '16px', border: '1px solid var(--border-light)', background: viewMode === 'weekday' ? 'var(--bg-layer2)' : 'transparent', color: 'var(--text-primary)' }}
        >
          By Weekday
        </button>
      </div>

      <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" style={{ minWidth: w, height: 'auto', display: 'block' }}>
          {/* Target Background */}
          <rect x={padL} y={yAt(targetMax)} width={innerW} height={yAt(targetMin) - yAt(targetMax)} fill="rgba(52,199,89,0.1)" />
          
          {/* Target Lines */}
          <line x1={padL} x2={w - padR} y1={yAt(targetMax)} y2={yAt(targetMax)} stroke="#ff9f0a" strokeDasharray="5 3" />
          <line x1={padL} x2={w - padR} y1={yAt(targetMin)} y2={yAt(targetMin)} stroke="#ff453a" strokeDasharray="5 3" />
          
          {/* Y Axis Labels */}
          <text x={4} y={yAt(400) + 4} fill="var(--text-secondary)" fontSize="10px">400</text>
          <text x={4} y={yAt(300) + 4} fill="var(--text-secondary)" fontSize="10px">300</text>
          <text x={4} y={yAt(200) + 4} fill="var(--text-secondary)" fontSize="10px">200</text>
          <text x={4} y={yAt(100) + 4} fill="var(--text-secondary)" fontSize="10px">100</text>
          <text x={4} y={yAt(40) + 4} fill="var(--text-secondary)" fontSize="10px">40</text>
          
          {/* X Axis Grid and Labels */}
          {Array.from({ length: 24 }).map((_, i) => {
             const x = xAt(i);
             const label = i === 0 ? '12AM' : i < 12 ? `${i}AM` : i === 12 ? '12PM' : `${i-12}PM`;
             return (
               <g key={i}>
                 <line x1={x} x2={x} y1={padT} y2={h - padB} stroke="var(--border-light)" strokeWidth={i % 6 === 0 ? 1 : 0.5} opacity={0.3} />
                 {i % 3 === 0 && (
                   <text x={x} y={h - 10} textAnchor="middle" fill="var(--text-secondary)" fontSize="10px">
                     {label}
                   </text>
                 )}
               </g>
             );
          })}

          {/* Data Lines */}
          {paths.map(p => p.d && (
            <path key={p.id} d={p.d} fill="none" stroke={p.color} strokeWidth={viewMode === 'weekday' ? 3 : 1.5} strokeLinejoin="round" />
          ))}
        </svg>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.5rem', marginTop: '1rem', fontSize: '0.85rem' }}>
        {viewMode === 'individual' ? (
          days.map((d, i) => (
             <label key={d.date} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-primary)' }}>
               <input type="checkbox" checked={selectedDays.has(d.date)} onChange={() => toggleDay(d.date)} />
               <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: DAY_COLORS[i % DAY_COLORS.length] }}></span>
               {d.date}
             </label>
          ))
        ) : (
          WEEKDAY_NAMES.map((name, i) => (
             <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-primary)' }}>
               <input type="checkbox" checked={selectedWeekdays.has(i)} onChange={() => toggleWeekday(i)} />
               <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: WEEKDAY_COLORS[i] }}></span>
               {name}
             </label>
          ))
        )}
      </div>
    </div>
  );
};

export default DailyOverlayChart;
